import { FC, useCallback, useEffect, useRef } from "react";
import { DrawingFinishEvent } from "@evergis/sgis/es/controls/Control";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";
import { useMapContext } from "@evergis/react";

import { BasePointCreator } from "../../../../../react-sgis/controls/baseControls/BasePointCreator";
import { ShadowedPointSymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";

import { useGeometrySelectionContext } from "../../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useSelectionEdit } from "./hooks/useSelectionEdit";
import { useGeometrySelection } from "../../../../../hooks/geometrySelection/useGeometrySelection";
import { createPointFeature } from "./utils/createPointFeature";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./hooks/useSelectionSymbols";

import { GeometrySelectionToolTypes, GeometrySelectionTypes } from "../types";
import { SelectionProps } from "./types";

export const PointSelection: FC<SelectionProps> = ({ mode }) => {
  const { addFeature, replaceFeature } = useGeometrySelectionContext();
  const [{ history }] = useGeometrySelection();
  const { map, painter } = useMapContext();
  const createControl = useRef<BasePointCreator>();
  const symbols = useSelectionSymbols();

  const switchMode = useCallback((create: boolean) => {
    createControl.current.isActive = create;
  }, []);

  const createFeature = useCallback(
    (event: DrawingFinishEvent<PointFeature>) => {
      const feature = createPointFeature(
        event.feature.position,
        map.crs,
        symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
      );

      if (mode === GeometrySelectionToolTypes.Replace && history.step > -1) {
        replaceFeature(feature);
      } else {
        addFeature(feature);
      }
    },
    [map.crs, symbols, mode, history.step, replaceFeature, addFeature],
  );

  const offEvents = useCallback(() => {
    createControl.current.off("drawingFinish", createFeature);
  }, [createFeature]);

  const cleanProperties = useCallback(() => {
    createControl.current = undefined;
  }, []);

  const initCreateMode = useCallback(() => {
    if (!createControl.current) {
      createControl.current = new BasePointCreator(map, painter, {
        isActive: false,
        useTempLayer: true,
        symbol: symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
        noLabel: true,
      });

      createControl.current.on("drawingFinish", createFeature);
    }
  }, [createFeature, map, painter]);

  useEffect(() => {
    initCreateMode();
    switchMode(true);

    return () => {
      switchMode(false);
      offEvents();
      cleanProperties();
    };
  }, [initCreateMode]); // eslint-disable-line

  useSelectionEdit(GeometrySelectionTypes.Point, map, painter, mode, switchMode);

  return null;
};
